import React, { useRef, useCallback, useEffect } from 'react';
import { css } from '@emotion/react';
import { posthog } from 'posthog-js';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { OnboardingButton } from '../Components/OnboardingButton';
import { colors, fontStyles, spacing, safeAreaInset } from '../../../styles';
import { useAutoRotate } from '../../PaymentModalByPreference/FeatureShowcase/useAutoRotate';
import { TIMING } from '../../PaymentModalByPreference/FeatureShowcase/constants';
import { AIChatAnimation } from '../../PaymentModalByPreference/FeatureShowcase/animations/AIChatAnimation';
import { DailyPicksAnimation } from '../../PaymentModalByPreference/FeatureShowcase/animations/DailyPicksAnimation';
import { NewsOnChartsAnimation } from '../../PaymentModalByPreference/FeatureShowcase/animations/NewsOnChartsAnimation';
import { SmartAlertsAnimation } from '../../PaymentModalByPreference/FeatureShowcase/animations/SmartAlertsAnimation';
import { PortfolioAnimation } from '../../PaymentModalByPreference/FeatureShowcase/animations/PortfolioAnimation';
import { MarketUpdatesAnimation } from '../../PaymentModalByPreference/FeatureShowcase/animations/MarketUpdatesAnimation';

const ANIMATION_COMPONENTS = [
  AIChatAnimation,
  DailyPicksAnimation,
  NewsOnChartsAnimation,
  SmartAlertsAnimation,
  PortfolioAnimation,
  MarketUpdatesAnimation,
] as const;

export const Welcome: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentIndex, progress, pause, resume, goTo, next, previous } = useAutoRotate({
    totalItems: ANIMATION_COMPONENTS.length,
    intervalMs: TIMING.autoRotateInterval,
  });

  // Track welcome screen view on mount
  useEffect(() => {
    posthog.capture('Welcome Screen Viewed');
  }, []);

  // Touch handling for swipe
  const touchStartX = useRef<number | null>(null);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      pause();
    },
    [pause],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current !== null) {
        const deltaX = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(deltaX) > TIMING.swipeThreshold) {
          if (deltaX > 0) {
            previous();
          } else {
            next();
          }
        }
        touchStartX.current = null;
      }
      resume();
    },
    [next, previous, resume],
  );

  const handleStart = () => {
    posthog.capture('Welcome Screen Continued');
    localStorage.removeItem('onboardingIndex'); // Reset progress
    history.push('/onboarding/0');
  };

  const handleSkip = () => {
    posthog.capture('onboarding_skipped_to_oto', { source: 'welcome_page' });
    localStorage.removeItem('onboardingIndex');
    // Clear any stale onboarding analytics data from a previous abandoned session.
    // onboardingStartTime is only written if not already set, so a stale value would
    // corrupt completionTime for any future onboarding attempt. Clear here rather than
    // relying on OTO's unmount path, which may not fire on back-gesture exits.
    localStorage.removeItem('onboardingMotivations');
    localStorage.removeItem('onboardingStartTime');
    history.push('/one-time-offer');
  };

  return (
    <div
      css={css`
        width: 100%;
        height: 100vh;
        margin: 0 auto;
        background-color: ${colors.sugar};
        position: relative;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        html[data-theme='dark'] & {
          background-color: ${colors.liquorice};
        }
      `}
    >
      {/* Animation Carousel */}
      <div
        css={css`
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: ${spacing.deka} ${spacing.deci} 0;
          max-height: 55%;

          @media (max-height: 700px) {
            max-height: 50%;
          }
        `}
      >
        {/* Animation container */}
        <div
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          css={css`
            position: relative;
            width: 100%;
            max-width: 360px;
            height: 380px;
            overflow: hidden;
            border-radius: 20px;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            box-shadow:
              0 1px 3px rgba(0, 0, 0, 0.04),
              0 4px 12px rgba(0, 0, 0, 0.06);

            html[data-theme='dark'] & {
              background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
              box-shadow:
                0 1px 3px rgba(0, 0, 0, 0.2),
                0 4px 12px rgba(0, 0, 0, 0.3);
            }

            @media (max-height: 700px) {
              height: 290px;
            }

            @media (max-height: 600px) {
              height: 250px;
            }
          `}
        >
          {/* Title overlay at top */}
          <div
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              z-index: 10;
              padding: ${spacing.deci} ${spacing.deci} ${spacing.centi};
              background: linear-gradient(
                180deg,
                rgba(248, 249, 250, 0.95) 0%,
                rgba(248, 249, 250, 0.8) 60%,
                rgba(248, 249, 250, 0) 100%
              );

              html[data-theme='dark'] & {
                background: linear-gradient(
                  180deg,
                  rgba(26, 26, 26, 0.95) 0%,
                  rgba(26, 26, 26, 0.8) 60%,
                  rgba(26, 26, 26, 0) 100%
                );
              }
            `}
          >
            <div
              css={css`
                ${fontStyles.lion};
                color: ${colors.liquorice};

                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              {
                [
                  t('welcome.featureChat'),
                  t('welcome.featureCopyTrading'),
                  t('welcome.featureNews'),
                  t('welcome.featureNotifications'),
                  t('welcome.featurePortfolio'),
                  t('welcome.featureDailyUpdate'),
                ][currentIndex]
              }
            </div>
          </div>

          {/* Animation area */}
          <div
            css={css`
              position: absolute;
              top: 48px;
              left: 0;
              right: 0;
              bottom: 0;
            `}
          >
            {ANIMATION_COMPONENTS.map((Component, index) => (
              <div
                key={index}
                css={css`
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  opacity: ${index === currentIndex ? 1 : 0};
                  transform: ${index === currentIndex ? 'translateX(0)' : 'translateX(8px)'};
                  transition:
                    opacity ${TIMING.transitionDuration}ms ease-out,
                    transform ${TIMING.transitionDuration}ms ease-out;
                  pointer-events: ${index === currentIndex ? 'auto' : 'none'};
                `}
              >
                <Component isActive={index === currentIndex} />
              </div>
            ))}
          </div>
        </div>

        {/* Navigation dots */}
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            gap: ${spacing.centi};
            margin-top: ${spacing.deci};
          `}
        >
          {ANIMATION_COMPONENTS.map((_, index) => {
            const isActive = index === currentIndex;

            return (
              <button
                key={index}
                type="button"
                onClick={() => goTo(index)}
                aria-label={t('welcome.viewFeature', { index: index + 1 })}
                css={css`
                  position: relative;
                  width: ${isActive ? '24px' : '8px'};
                  height: 8px;
                  border: none;
                  padding: 0;
                  cursor: pointer;
                  background: ${isActive ? 'transparent' : colors.cloud};
                  border-radius: 4px;
                  transition: all 0.3s ease;
                  overflow: hidden;

                  html[data-theme='dark'] & {
                    background: ${isActive ? 'transparent' : colors.tungsten};
                  }

                  &:focus {
                    outline: none;
                  }
                `}
              >
                {isActive && (
                  <>
                    {/* Background track */}
                    <div
                      css={css`
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background: ${colors.cloud};
                        border-radius: 4px;

                        html[data-theme='dark'] & {
                          background: ${colors.tungsten};
                        }
                      `}
                    />
                    {/* Progress fill */}
                    <div
                      css={css`
                        position: absolute;
                        top: 0;
                        left: 0;
                        height: 100%;
                        width: ${progress}%;
                        background: ${colors.petrolBlue};
                        border-radius: 4px;
                        transition: width 50ms linear;

                        html[data-theme='dark'] & {
                          background: ${colors.skyBlue};
                        }
                      `}
                    />
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Overlay */}
      <div
        css={css`
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 20px 24px calc(${safeAreaInset.bottom} + 16px);
          z-index: 2;
          background: ${colors.sugar};

          html[data-theme='dark'] & {
            background: ${colors.liquorice};
          }
        `}
      >
        {/* App Title */}
        <div
          css={css`
            text-align: center;
            margin-bottom: 16px;
          `}
        >
          <h1
            css={css`
              ${fontStyles.elephant};
              color: ${colors.liquorice};
              margin-bottom: ${spacing.centi};
              position: sticky;
              top: 0;
              z-index: 5;
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {t('welcome.heading')}
          </h1>
          <p
            css={css`
              ${fontStyles.horse};
              color: ${colors.silver};
              line-height: 1.4;
              font-weight: 400;
              margin: 0;
              html[data-theme='dark'] & {
                color: ${colors.cloud};
              }
            `}
          >
            {t('welcome.subheading')}
          </p>

          {/* Feature badges */}
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
              justify-content: center;
              gap: 8px 12px;
              margin-top: ${spacing.deci};
            `}
          >
            {[
              t('welcome.badgeFundamentals'),
              t('welcome.badgeEarnings'),
              t('welcome.badgeTechnicals'),
              t('welcome.badgeOptions'),
              t('welcome.badgeSentiment'),
              t('welcome.badgeNews'),
            ].map((feature) => (
              <span
                key={feature}
                css={css`
                  ${fontStyles.cat};
                  color: ${colors.petrolBlue};
                  display: inline-flex;
                  align-items: center;
                  gap: 4px;

                  html[data-theme='dark'] & {
                    color: ${colors.skyBlue};
                  }
                `}
              >
                <span
                  css={css`
                    color: ${colors.leaf};
                  `}
                >
                  ✓
                </span>
                {feature}
              </span>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
        >
          <OnboardingButton onClick={handleStart}>{t('welcome.setupWatchlist')}</OnboardingButton>
          <button
            onClick={handleSkip}
            css={css`
              background: none;
              border: none;
              color: ${colors.silver};
              ${fontStyles.dog};
              cursor: pointer;
              padding: ${spacing.centi};

              &:hover {
                color: ${colors.petrolBlue};
              }

              html[data-theme='dark'] & {
                &:hover {
                  color: ${colors.skyBlue};
                }
              }
            `}
          >
            {t('welcome.skipExplore')}
          </button>
        </div>
      </div>
    </div>
  );
};
